import React, { useEffect, useRef, useContext, useState } from "react";
import styled from "styled-components";
import gsap, { SplitText } from "gsap/all";
import { FontsLoadedContext, ScreenContext } from "components/Providers";

import { useMedia } from "utils/hooks";

import colors from "styles/colors";
import text from "styles/text";
import media from "styles/media";

import ArrowSVG from "assets/images/icons/bitArrow.svg";
import DiamondWEBM from 'assets/videos/diamond_VP9.webm'
import DiamondMP4 from 'assets/videos/diamond-hvc1.mp4'
import DiamondWEBP from 'assets/images/diamond.webp'

const Powers: React.FC = () => {
  const wrapperRef = useRef<HTMLElement | null>(null);
  const [diamondRef, setDiamondRef] = useState<HTMLVideoElement | HTMLImageElement | null | undefined>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  const fontsLoaded = useContext(FontsLoadedContext);
  const screen = useContext(ScreenContext)

  const titleText = useMedia("$FCD powers the FreshCut ecosystem", "$FCD powers the FreshCut ecosystem", "$FCD powers the FreshCut Platform", "$FCD powers the FreshCut Platform and ecosystem");

  const end = useMedia("bottom bottom", "bottom bottom", "bottom bottom-=20%", "bottom bottom");
  const start = useMedia("top 30%", "top 30%", "top 60%", "top 70%");

  useEffect(() => {
    if (fontsLoaded && diamondRef) {
      const textSplitChild = new SplitText(textRef.current, {
        type: "lines",
        linesClass: "powers-text-child",
      });

      new SplitText(textRef.current, {
        type: "lines",
        linesClass: "powers-text-parent",
      });

      const titleSplit = new SplitText(titleRef.current, {
        type: "lines",
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          scroller: ".smooth-scroll",
          trigger: wrapperRef.current,
          scrub: true,
          start: start,
          end: end,
        },
      });

      tl.to(
        titleSplit.lines,
        {
          stagger: .5,
          duration: 1,
          color: colors.pureWhite,
        },
        0
      );

      tl.from(
        diamondRef,
        {
          duration: 1,
          top: "100%",
          scale: 0.25,
        },
        1
      );

      tl.from(
        textSplitChild.lines,
        {
          stagger: 0.25,
          duration: 0.5,
          y: "100%",
        },
        1.5
      );

      return () => {
        tl.kill();
      };
    }
  }, [fontsLoaded, end, start, diamondRef]);

  return (
    <Wrapper ref={wrapperRef}>
      <Kicker>
        <Arrow src={ArrowSVG} alt="bit arrow" loading="lazy"/>
        <p>What is $FCD?</p>
        <Arrow src={ArrowSVG} alt="bit arrow" loading="lazy"/>
      </Kicker>
      <Title ref={titleRef}>{titleText}</Title>
      <Text ref={textRef}>
        FreshCut users can earn $FCD through a variety of digital and IRL activities while interacting with fans and creators, unlocking membership benefits and gaining access to the FreshCut network
        of experiences
      </Text>
      {(screen.desktop || screen.fullWidth) && 
        <Video ref={ref => setDiamondRef(ref)} autoPlay playsInline muted loop controls={false} poster={DiamondWEBP} preload="none">
          <source src={DiamondMP4} type="video/mp4; codecs=hvc1"/>
          <source src={DiamondWEBM} type="video/webm; codecs=vp9"/>
        </Video>
      }
      {(screen.mobile || screen.tablet) && 
        <Image ref={ref => setDiamondRef(ref)} src={DiamondWEBP} alt="diamond" loading="lazy"/>
      }
    </Wrapper>
  );
};

export default Powers;

const Wrapper = styled.section`
  width: 100vw;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${colors.pureBlack};

  padding-top: 19.44vw;
  padding-left: 4.17vw;
  padding-right: 4.17vw;
  padding-bottom: 12.29vw;

  ${media.tablet} {
    padding-top: 24.41vw;
    padding-left: 5.86vw;
    padding-right: 5.86vw;
    padding-bottom: 21.97vw;
  }
  ${media.mobile} {
    padding: 43.2vw 4vw 32vw;
  }
`;

const Kicker = styled.p`
  ${text.desktop.accent2}
  color: ${colors.pureWhite};
  display: flex;
  align-items: center;
  text-transform: uppercase;

  margin-bottom: 2.78vw;

  p {
    margin-right: 0.35vw;
    margin-left: 0.35vw;
  }

  ${media.tablet} {
    ${text.tablet.accent2}
    margin-bottom: 3.91vw;
    left: 5.859vw;
    top: 0vw;
  }
  ${media.mobile} {
    margin-bottom: 5.333vw;
    ${text.mobile.accent3};
  }
`;

const Arrow = styled.img`
  transform: rotate(-90deg);
  transform-origin: center;

  height: 1.27vw;
  width: 1.08vw;
  ${media.mobile} {
    width: 3.733vw;
    height: 2.933vw;
  }
`;

const Title = styled.h2`
  position: relative;
  z-index: 3;
  ${text.desktop.h3}
  text-transform: uppercase;

  div {
    color: ${colors.black50};
  }

  width: 72vw;
  margin-bottom: 4.17vw;

  ${media.tablet} {
    ${text.tablet.h3}
    letter-spacing: -0.05em;
    width: 95.508vw;
    margin-bottom: 5.86vw;
  }
  ${media.mobile} {
    ${text.mobile.h5}
    width: 100%;
    margin-bottom: 16vw;
  }
`;

const Text = styled.p`
  position: relative;
  z-index: 1;
  ${text.desktop.bodyL}
  box-sizing: border-box;

  .powers-text-parent {
    overflow: hidden;
  }

  .powers-text-child {
    background: ${colors.horzGradient};
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  width: 54.51vw;

  ${media.tablet} {
    ${text.tablet.bodyL}
    z-index: 2;
    width: 88.28vw;
    height: 16.406vw;
  }
  ${media.mobile} {
    ${text.mobile.bodyR}
    width: 92vw;
    height: 44.8vw;
    z-index: 2;
  }
`;

const DiamondStyles = `
  position: absolute;
  mix-blend-mode: screen;
  z-index: 5;
  transform: translate3d(0,0,0);
  width: 45.14vw;
  right: 4.17vw;
  top: 28.19vw;

  ${media.tablet} {
    z-index: 1;
    width: 100vw;
    right: -42.133vw;
    top: 30.297vw;
  }
  ${media.mobile} {
    width: 53.333vw;
    height: auto;
    right: -20vw;
    top: 80.667vw;
  }
`;

const Video = styled.video`
  ${DiamondStyles}
`

const Image = styled.img`
  ${DiamondStyles}
`
